import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "@/lib/api/client";
import { resolvePostLoginPath } from "@/lib/auth/landing";
import { getStoredRefreshToken, useAuthStore } from "@/lib/auth/authStore";
import { queryClient } from "@/lib/query/queryClient";
import {
  createFunctionalAccount,
  forgotPassword,
  getActiveContext,
  getCurrentSession,
  getFunctionalAccounts,
  login,
  logout,
  registerAccount,
  resetPassword,
  selectActiveProfile,
  switchFunctionalAccount,
  switchOrganization,
} from "@/features/auth/api";
import type {
  AuthResponse,
  CreateFunctionalAccountPayload,
  SelectProfileResponse,
  SwitchFunctionalAccountPayload,
  SwitchOrganizationPayload,
} from "@/features/auth/types";

export const loadAccountContextForCurrentSession = async (): Promise<void> => {
  const [accountsResponse, contextResponse] = await Promise.all([
    getFunctionalAccounts(),
    getActiveContext(),
  ]);

  useAuthStore.getState().setAccountContext({
    identity: contextResponse.identity,
    availableFunctionalAccounts: accountsResponse.items,
    activeContext: contextResponse.active_context ?? accountsResponse.active_context,
  });
};

const applyAuthResponse = (response: AuthResponse): void => {
  const store = useAuthStore.getState();
  const tokens = response.tokens;

  if (!tokens?.access_token || !tokens?.refresh_token) {
    throw new Error("Përgjigjja e hyrjes nuk përmban token valid.");
  }

  store.setTokens({
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
  });
  store.setUser({
    id: response.account_id,
    accountId: response.account_id,
    systemRole: response.system_role,
    profileRole: response.profile_role,
    businessRole: response.business_role,
    verified: false,
    permissions: [],
    isImpersonating: false,
    impersonatedBy: null,
  });
  store.setActiveRoles({
    systemRole: response.system_role,
    profileRole: response.profile_role,
    businessRole: response.business_role,
  });
  store.setBootstrapState({ isBootstrapping: false, hasBootstrapped: true });
};

const applySelectedProfile = (response: SelectProfileResponse): void => {
  const store = useAuthStore.getState();

  store.setUser(
    store.user
      ? {
          ...store.user,
          systemRole: response.system_role,
          profileRole: response.profile_role,
          businessRole: response.business_role,
        }
      : null,
  );
  store.setActiveRoles({
    systemRole: response.system_role,
    profileRole: response.profile_role,
    businessRole: response.business_role,
  });
};

const applyCurrentSession = (response: Awaited<ReturnType<typeof getCurrentSession>>): void => {
  const store = useAuthStore.getState();

  store.setUser({
    id: response.account_id,
    accountId: response.account_id,
    systemRole: response.system_role,
    profileRole: response.profile_role,
    businessRole: response.business_role,
    verified: response.verified,
    permissions: response.permissions,
    email: response.email ?? undefined,
    fullName: response.full_name ?? undefined,
    avatarUrl: response.avatar_url ?? null,
    patientId: response.patient_id,
    clinicianId: response.clinician_id,
    isImpersonating: response.is_impersonating,
    impersonatedBy: response.impersonated_by,
  });
  store.setActiveRoles({
    systemRole: response.system_role,
    profileRole: response.profile_role,
    businessRole: response.business_role,
  });
};

export const refreshAuthStateForCurrentSession = async (): Promise<void> => {
  const sessionResponse = await getCurrentSession();
  applyCurrentSession(sessionResponse);
  // ActiveContext duhet te aplikohet i fundit, sepse perfaqeson account-in
  // aktual dhe nuk duhet te mbishkruhet nga legacy fields e /auth/me.
  await loadAccountContextForCurrentSession();
};

export const invalidateAccountContextQueries = async (): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["auth"] }),
    queryClient.invalidateQueries({ queryKey: ["auth", "bootstrap"] }),
    queryClient.invalidateQueries({ queryKey: ["auth", "accounts"] }),
    queryClient.invalidateQueries({ queryKey: ["capabilities"] }),
    queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    queryClient.invalidateQueries({ queryKey: ["msn"] }),
    queryClient.invalidateQueries({ queryKey: ["notifications"] }),
    queryClient.invalidateQueries({ queryKey: ["patient", "home"] }),
    queryClient.invalidateQueries({ queryKey: ["clinician"] }),
    queryClient.invalidateQueries({ queryKey: ["clinical"] }),
    queryClient.invalidateQueries({ queryKey: ["professional"] }),
    queryClient.invalidateQueries({ queryKey: ["businesses", "owned"] }),
    queryClient.invalidateQueries({ queryKey: ["service-hub"] }),
  ]);
};

/**
 * Menaxhon mutacionin e login me TanStack Query.
 */
export const useLoginMutation = (options?: { redirectTo?: string; onSuccess?: () => void }) => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: login,
    onSuccess: async (response) => {
      applyAuthResponse(response);
      await loadAccountContextForCurrentSession();
      options?.onSuccess?.();
      navigate(
        resolvePostLoginPath(options?.redirectTo, {
          systemRole: response.system_role,
          profileRole: response.profile_role,
          businessRole: response.business_role,
        }),
        { replace: true },
      );
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });
};

/**
 * Menaxhon mutacionin e regjistrimit me TanStack Query.
 */
export const useRegisterMutation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: registerAccount,
    onSuccess: async (response) => {
      applyAuthResponse(response);
      await loadAccountContextForCurrentSession();
      navigate("/app/dashboard", { replace: true });
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });
};

/**
 * Mbyll sesionin ne backend kur ka refresh token dhe pastron cache-in sensitiv lokal.
 */
export const useLogoutMutation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      const refreshToken = getStoredRefreshToken();

      if (refreshToken) {
        await logout(refreshToken);
      }
    },
    onSettled: () => {
      useAuthStore.getState().logoutLocal();
      queryClient.clear();
      navigate("/", { replace: true });   // → faqja kryesore, jo /login
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });
};

/**
 * Nis rrjedhen e harrimit te fjalekalimit.
 */
export const useForgotPasswordMutation = () =>
  useMutation({
    mutationFn: forgotPassword,
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });

/**
 * Kryen reset te fjalekalimit dhe kthen perdoruesin te login.
 */
export const useResetPasswordMutation = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      navigate("/login", { replace: true, state: { successMessageKey: "auth.passwordChangedSuccess" } });
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });
};

/**
 * Vendos profilin aktiv te onboarding-ut pa shpikur role ne klient.
 */
export const useSelectProfileMutation = () =>
  useMutation({
    mutationFn: selectActiveProfile,
    onSuccess: async (response) => {
      applySelectedProfile(response);
      await loadAccountContextForCurrentSession();
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });

export const useCreateFunctionalAccountMutation = () =>
  useMutation({
    mutationFn: (payload: CreateFunctionalAccountPayload) => createFunctionalAccount(payload),
    onSuccess: async () => {
      await loadAccountContextForCurrentSession();
      await queryClient.invalidateQueries({ queryKey: ["auth", "accounts"] });
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });

export const useSwitchFunctionalAccountMutation = () =>
  useMutation({
    mutationFn: (payload: SwitchFunctionalAccountPayload) => switchFunctionalAccount(payload),
    onSuccess: async (response) => {
      const store = useAuthStore.getState();

      if (response.access_token) {
        store.setAccessToken(response.access_token);
      }
      store.setAccountContext({ activeContext: response.active_context });
      await refreshAuthStateForCurrentSession();
      await invalidateAccountContextQueries();
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });

export const useSwitchOrganizationMutation = () =>
  useMutation({
    mutationFn: (payload: SwitchOrganizationPayload) => switchOrganization(payload),
    onSuccess: async (response) => {
      const store = useAuthStore.getState();

      if (response.access_token) {
        store.setAccessToken(response.access_token);
      }
      store.setAccountContext({ activeContext: response.active_context });
      await refreshAuthStateForCurrentSession();
      await invalidateAccountContextQueries();
    },
    meta: {
      getErrorMessage: getApiErrorMessage,
    },
  });
